import React, { useState, useEffect } from 'react';
import { Trash2, Mail, Users, UserPlus, CheckCircle, Clock, XCircle } from 'lucide-react';
import { FamilyMember } from '@fitzaa/shared';
import { useTheme } from '../contexts/ThemeContext';
import {
    inviteFamilyMemberWeb,
    getFamilyMembersWeb,
    removeFamilyMemberWeb,
    resendFamilyInvitationWeb,
    cancelFamilyInvitationWeb
} from '../services/WebCloudFunctionsService';

interface FamilyMemberManagementProps {
    orgUserId: string;
    organizationName: string;
}

export const FamilyMemberManagement: React.FC<FamilyMemberManagementProps> = ({
    orgUserId,
    organizationName
}) => {
    const { theme } = useTheme();
    const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isInviting, setIsInviting] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteName, setInviteName] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [memberToRemove, setMemberToRemove] = useState<{ id: string; name: string } | null>(null);
    
    // Detect mobile screen size
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        loadFamilyMembers();
    }, []);

    const loadFamilyMembers = async () => {
        setIsLoading(true);
        setError(null);

        try {
            console.log('🔍 [Web] Loading family members for orgUserId:', orgUserId);
            const result = await getFamilyMembersWeb(orgUserId) as any;
            console.log('🔍 [Web] Family members result:', result);
            if (result.success) {
                console.log('🔍 [Web] Setting family members:', result.familyMembers);
                setFamilyMembers(result.familyMembers || []);
            } else {
                console.error('❌ [Web] Failed to load family members:', result.error);
                setError(result.error || 'Failed to load family members');
            }
        } catch (err) {
            console.error('❌ [Web] Error loading family members:', err);
            setError('An unexpected error occurred');
        } finally {
            setIsLoading(false);
        }
    };

    const handleInviteFamilyMember = async () => {
        if (!inviteEmail.trim()) {
            setError('Email address is required');
            return;
        }

        setIsInviting(true);
        setError(null);
        setSuccess(null);

        try {
            const result = await inviteFamilyMemberWeb(inviteEmail, inviteName || undefined) as any;
            if (result.success) {
                setSuccess(`Invitation sent to ${inviteEmail}`);
                setInviteEmail('');
                setInviteName('');
                loadFamilyMembers();
            } else {
                setError(result.error || 'Failed to send invitation');
            }
        } catch (err) {
            setError('An unexpected error occurred');
        } finally {
            setIsInviting(false);
        }
    };

    const handleRemoveFamilyMember = (familyMemberId: string, memberName: string) => {
        setMemberToRemove({ id: familyMemberId, name: memberName });
        setShowConfirmDialog(true);
    };

    const confirmRemoveFamilyMember = async () => {
        if (!memberToRemove) return;

        setError(null);
        setSuccess(null);

        try {
            const result = await removeFamilyMemberWeb(memberToRemove.id, orgUserId) as any;
            if (result.success) {
                setSuccess(`${memberToRemove.name} has been removed from your family members`);
                loadFamilyMembers();
            } else {
                setError(result.error || 'Failed to remove family member');
            }
        } catch (err) {
            setError('An unexpected error occurred');
        } finally {
            setShowConfirmDialog(false);
            setMemberToRemove(null);
        }
    };

    const cancelRemoveFamilyMember = () => {
        setShowConfirmDialog(false);
        setMemberToRemove(null);
    };

    const handleResendInvitation = async (invitationId: string, memberName: string) => {
        setError(null);
        setSuccess(null);

        try {
            const result = await resendFamilyInvitationWeb(invitationId) as any;
            if (result.success) {
                setSuccess(`Invitation resent to ${memberName}`);
            } else {
                setError(result.error || 'Failed to resend invitation');
            }
        } catch (err) {
            setError('An unexpected error occurred');
        }
    };

    const handleCancelInvitation = async (invitationId: string, memberName: string) => {
        setError(null);
        setSuccess(null);

        try {
            const result = await cancelFamilyInvitationWeb(invitationId) as any;
            if (result.success) {
                setSuccess(`Invitation cancelled for ${memberName}`);
                loadFamilyMembers();
            } else {
                setError(result.error || 'Failed to cancel invitation');
            }
        } catch (err) {
            setError('An unexpected error occurred');
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'accepted':
                return <CheckCircle size={16} color={theme.colors.success} />;
            case 'pending':
                return <Clock size={16} color={theme.colors.warning} />;
            case 'declined':
                return <XCircle size={16} color={theme.colors.error} />;
            default:
                return <Clock size={16} color={theme.colors.textTertiary} />;
        }
    };

    if (isLoading) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: theme.spacing.xxl,
            }}>
                <div style={{
                    width: '32px',
                    height: '32px',
                    border: `3px solid ${theme.colors.border}`,
                    borderTop: `3px solid ${theme.colors.primary}`,
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                }} />
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Error/Success Messages */}
            {error && (
                <div style={{
                    padding: '12px 16px',
                    backgroundColor: `${theme.colors.error}10`,
                    border: `1px solid ${theme.colors.error}`,
                    borderRadius: '12px',
                    color: theme.colors.error,
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: theme.fonts.primary,
                }}>
                    {error}
                </div>
            )}
            {success && (
                <div style={{
                    padding: '12px 16px',
                    backgroundColor: `${theme.colors.success}10`,
                    border: `1px solid ${theme.colors.success}`,
                    borderRadius: '12px',
                    color: theme.colors.success,
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: theme.fonts.primary,
                }}>
                    {success}
                </div>
            )}

            {/* Family Members Card */}
            <div style={{
                backgroundColor: theme.colors.surface,
                borderRadius: isMobile ? '12px' : '16px',
                padding: isMobile ? '12px' : '20px',
                border: `1px solid ${theme.colors.border}`,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: isMobile ? '12px' : '20px',
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                    gap: isMobile ? '8px' : '0'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}>
                        <div style={{
                            width: isMobile ? '32px' : '40px',
                            height: isMobile ? '32px' : '40px',
                            borderRadius: isMobile ? '10px' : '12px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                        }}>
                            <Users size={isMobile ? 16 : 20} color="#ffffff" />
                        </div>
                        <div>
                            <h3 style={{
                                color: theme.colors.text,
                                margin: 0,
                                fontSize: theme.typography.h3.fontSize,
                                fontWeight: theme.typography.h3.fontWeight,
                                fontFamily: theme.fonts.primary,
                                lineHeight: theme.typography.h3.lineHeight,
                            }}>
                                Family Members
                            </h3>
                            <p style={{
                                color: theme.colors.textSecondary,
                                margin: '2px 0 0 0',
                                fontSize: isMobile ? theme.typography.caption.fontSize : theme.typography.body.fontSize,
                                fontWeight: theme.typography.caption.fontWeight,
                                fontFamily: theme.fonts.secondary,
                                lineHeight: theme.typography.caption.lineHeight,
                            }}>
                                {familyMembers.length} {familyMembers.length === 1 ? 'member' : 'members'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={loadFamilyMembers}
                        style={{
                            color: theme.colors.primary,
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: isMobile ? theme.typography.caption.fontSize : theme.typography.body.fontSize,
                            fontWeight: theme.typography.button.fontWeight,
                            fontFamily: theme.fonts.primary,
                            padding: isMobile ? '4px 8px' : '6px 12px',
                            borderRadius: '8px',
                            transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = `${theme.colors.primary}10`;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                    >
                        Refresh
                    </button>
                </div>

                {/* Compact Invite Form */}
                <div style={{
                    padding: isMobile ? '12px' : '16px',
                    background: `linear-gradient(135deg, ${theme.colors.primary}08 0%, ${theme.colors.primary}03 100%)`,
                    borderRadius: isMobile ? '10px' : '12px',
                    border: `1px solid ${theme.colors.primary}20`,
                    marginBottom: isMobile ? '16px' : '24px',
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'stretch' : 'flex-end',
                        gap: isMobile ? '10px' : '12px'
                    }}>
                        <div style={{ flex: '1', minWidth: isMobile ? '100%' : '200px' }}>
                            <label style={{
                                display: 'block',
                                color: theme.colors.text,
                                marginBottom: isMobile ? '4px' : '6px',
                                fontWeight: theme.typography.body.fontWeight,
                                fontSize: isMobile ? theme.typography.caption.fontSize : theme.typography.body.fontSize,
                                fontFamily: theme.fonts.primary,
                            }}>
                                Email Address
                            </label>
                            <input
                                type="email"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                placeholder="family@example.com"
                                style={{
                                    width: '100%',
                                    padding: isMobile ? '8px 12px' : '10px 14px',
                                    border: `1px solid ${theme.colors.border}`,
                                    borderRadius: isMobile ? '8px' : '10px',
                                    backgroundColor: theme.colors.surface,
                                    color: theme.colors.text,
                                    fontSize: isMobile ? theme.typography.body.fontSize : theme.typography.body.fontSize,
                                    fontFamily: theme.fonts.primary,
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                    transition: 'all 0.2s ease',
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = theme.colors.primary;
                                    e.target.style.boxShadow = `0 0 0 3px ${theme.colors.primary}20`;
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = theme.colors.border;
                                    e.target.style.boxShadow = 'none';
                                }}
                            />
                        </div>
                        <div style={{ flex: '1', minWidth: isMobile ? '100%' : '180px' }}>
                            <label style={{
                                display: 'block',
                                color: theme.colors.text,
                                marginBottom: isMobile ? '4px' : '6px',
                                fontWeight: theme.typography.body.fontWeight,
                                fontSize: isMobile ? theme.typography.caption.fontSize : theme.typography.body.fontSize,
                                fontFamily: theme.fonts.primary,
                            }}>
                                Name (Optional)
                            </label>
                            <input
                                type="text"
                                value={inviteName}
                                onChange={(e) => setInviteName(e.target.value)}
                                placeholder="John Doe"
                                style={{
                                    width: '100%',
                                    padding: isMobile ? '8px 12px' : '10px 14px',
                                    border: `1px solid ${theme.colors.border}`,
                                    borderRadius: isMobile ? '8px' : '10px',
                                    backgroundColor: theme.colors.surface,
                                    color: theme.colors.text,
                                    fontSize: isMobile ? theme.typography.body.fontSize : theme.typography.body.fontSize,
                                    fontFamily: theme.fonts.primary,
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                    transition: 'all 0.2s ease',
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = theme.colors.primary;
                                    e.target.style.boxShadow = `0 0 0 3px ${theme.colors.primary}20`;
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = theme.colors.border;
                                    e.target.style.boxShadow = 'none';
                                }}
                            />
                        </div>
                        <button
                            onClick={handleInviteFamilyMember}
                            disabled={!inviteEmail.trim() || isInviting}
                            style={{
                                border: 'none',
                                borderRadius: '999px',
                                padding: isMobile ? '8px 16px' : '10px 20px',
                                fontSize: isMobile ? theme.typography.button.fontSize : theme.typography.button.fontSize,
                                fontWeight: theme.typography.button.fontWeight,
                                fontFamily: theme.fonts.primary,
                                lineHeight: theme.typography.button.lineHeight,
                                cursor: !inviteEmail.trim() || isInviting ? 'not-allowed' : 'pointer',
                                backgroundImage: !inviteEmail.trim() || isInviting 
                                    ? 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'
                                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: '#ffffff',
                                boxShadow: !inviteEmail.trim() || isInviting 
                                    ? '0 4px 12px rgba(148, 163, 184, 0.25)'
                                    : '0 8px 20px rgba(102, 126, 234, 0.35)',
                                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                position: 'relative',
                                overflow: 'hidden',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                opacity: !inviteEmail.trim() || isInviting ? 0.6 : 1,
                                whiteSpace: 'nowrap',
                                height: isMobile ? '36px' : '42px',
                                width: isMobile ? '100%' : 'auto',
                            }}
                            onMouseEnter={(e) => {
                                if (!(!inviteEmail.trim() || isInviting)) {
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                    e.currentTarget.style.boxShadow = '0 12px 28px rgba(102, 126, 234, 0.45)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = !inviteEmail.trim() || isInviting 
                                    ? '0 4px 12px rgba(148, 163, 184, 0.25)'
                                    : '0 8px 20px rgba(102, 126, 234, 0.35)';
                            }}
                        >
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: '-100%',
                                    width: '100%',
                                    height: '100%',
                                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)',
                                    animation: !inviteEmail.trim() || isInviting ? 'none' : 'shine 2s infinite',
                                }}
                            />
                            {isInviting ? (
                                <>
                                    <div style={{
                                        width: '14px',
                                        height: '14px',
                                        border: '2px solid #ffffff',
                                        borderTop: '2px solid transparent',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite',
                                        position: 'relative',
                                        zIndex: 1,
                                    }} />
                                    <span style={{ position: 'relative', zIndex: 1 }}>Sending...</span>
                                </>
                            ) : (
                                <>
                                    <UserPlus size={isMobile ? 14 : 16} style={{ position: 'relative', zIndex: 1 }} />
                                    <span style={{ position: 'relative', zIndex: 1 }}>Invite</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {familyMembers.length === 0 ? (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: isMobile ? '32px 16px' : '48px 32px',
                        background: `linear-gradient(135deg, ${theme.colors.primary}05 0%, transparent 100%)`,
                        borderRadius: isMobile ? '10px' : '12px',
                        border: `1px dashed ${theme.colors.border}`,
                    }}>
                        <div style={{
                            width: isMobile ? '48px' : '64px',
                            height: isMobile ? '48px' : '64px',
                            margin: '0 auto 12px',
                            borderRadius: '50%',
                            background: `linear-gradient(135deg, ${theme.colors.primary}15 0%, ${theme.colors.primary}08 100%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <Users size={isMobile ? 24 : 32} color={theme.colors.primary} style={{ opacity: 0.6 }} />
                        </div>
                        <p style={{
                            color: theme.colors.text,
                            margin: '0 0 6px 0',
                            fontSize: isMobile ? theme.typography.h3.fontSize : theme.typography.h2.fontSize,
                            fontWeight: theme.typography.h3.fontWeight,
                            fontFamily: theme.fonts.display,
                        }}>
                            No family members yet
                        </p>
                        <p style={{
                            color: theme.colors.textSecondary,
                            margin: 0,
                            fontSize: isMobile ? theme.typography.caption.fontSize : theme.typography.body.fontSize,
                            fontWeight: theme.typography.body.fontWeight,
                            fontFamily: theme.fonts.secondary,
                        }}>
                            Invite someone to get started!
                        </p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '8px' : '10px' }}>
                        {familyMembers.map((member) => (
                            <div
                                key={member.id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: isMobile ? '10px 12px' : '14px 16px',
                                    backgroundColor: theme.colors.card || theme.colors.surface,
                                    borderRadius: isMobile ? '10px' : '12px',
                                    border: `1px solid ${theme.colors.border}`,
                                    transition: 'all 0.2s ease',
                                    cursor: 'default',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = theme.colors.primary + '40';
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = theme.colors.border;
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '14px', flex: 1, minWidth: 0 }}>
                                    <div style={{
                                        width: isMobile ? '36px' : '44px',
                                        height: isMobile ? '36px' : '44px',
                                        background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primary}dd 100%)`,
                                        borderRadius: isMobile ? '10px' : '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: `0 2px 8px ${theme.colors.primary}30`,
                                        flexShrink: 0
                                    }}>
                                        <span style={{
                                            color: '#ffffff',
                                            fontWeight: '700',
                                            fontSize: isMobile ? '14px' : '18px',
                                            fontFamily: theme.fonts.primary,
                                        }}>
                                            {member.displayName.charAt(0).toUpperCase()}
                                        </span>
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: isMobile ? 'column' : 'row',
                                            alignItems: isMobile ? 'flex-start' : 'center',
                                            gap: isMobile ? '4px' : '8px',
                                            marginBottom: isMobile ? '2px' : '4px'
                                        }}>
                                            <p style={{
                                                color: theme.colors.text,
                                                margin: 0,
                                                fontWeight: theme.typography.body.fontWeight,
                                                fontSize: isMobile ? theme.typography.body.fontSize : theme.typography.h3.fontSize,
                                                fontFamily: theme.fonts.primary,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {member.displayName}
                                            </p>
                                            <div style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                padding: isMobile ? '2px 6px' : '2px 8px',
                                                borderRadius: '6px',
                                                backgroundColor: member.status === 'accepted' 
                                                    ? `${theme.colors.success}15`
                                                    : member.status === 'pending'
                                                    ? `${theme.colors.warning}15`
                                                    : `${theme.colors.error}15`,
                                                flexShrink: 0
                                            }}>
                                                {getStatusIcon(member.status)}
                                                <span style={{
                                                    fontWeight: '600',
                                                    textTransform: 'capitalize',
                                                    color: member.status === 'accepted'
                                                        ? theme.colors.success
                                                        : member.status === 'pending'
                                                        ? theme.colors.warning
                                                        : theme.colors.error,
                                                    fontSize: isMobile ? '9px' : '11px',
                                                    fontFamily: theme.fonts.primary,
                                                    letterSpacing: '0.3px',
                                                }}>
                                                    {member.status}
                                                </span>
                                            </div>
                                        </div>
                                        <p style={{
                                            color: theme.colors.textSecondary,
                                            margin: 0,
                                            fontSize: isMobile ? theme.typography.caption.fontSize : theme.typography.body.fontSize,
                                            fontWeight: theme.typography.caption.fontWeight,
                                            fontFamily: theme.fonts.secondary,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {member.email}
                                        </p>
                                        <p style={{
                                            color: theme.colors.textSecondary,
                                            margin: isMobile ? '2px 0 0 0' : '4px 0 0 0',
                                            fontSize: isMobile ? theme.typography.caption.fontSize : theme.typography.caption.fontSize,
                                            fontWeight: theme.typography.caption.fontWeight,
                                            fontFamily: theme.fonts.secondary,
                                        }}>
                                            {member.status === 'accepted' && member.acceptedAt
                                                ? `Joined ${new Date(member.acceptedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                                                : `Invited ${new Date(member.invitedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                                            }
                                        </p>
                                    </div>
                                </div>

                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: isMobile ? '4px' : '8px',
                                    marginLeft: isMobile ? '8px' : '12px',
                                    flexShrink: 0
                                }}>
                                    {member.status === 'accepted' ? (
                                        <button
                                            onClick={() => handleRemoveFamilyMember(member.id, member.displayName)}
                                            style={{
                                                border: 'none',
                                                borderRadius: '999px',
                                                padding: isMobile ? '6px' : '8px',
                                                width: isMobile ? '32px' : '36px',
                                                height: isMobile ? '32px' : '36px',
                                                cursor: 'pointer',
                                                backgroundImage: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                                color: '#ffffff',
                                                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                                                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-1px) scale(1.05)';
                                                e.currentTarget.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
                                            }}
                                        >
                                            <Trash2 size={isMobile ? 14 : 16} />
                                        </button>
                                    ) : (
                                        <div style={{ display: 'flex', gap: isMobile ? '4px' : '8px' }}>
                                            <button
                                                onClick={() => handleResendInvitation(member.id, member.displayName)}
                                                style={{
                                                    border: 'none',
                                                    borderRadius: '999px',
                                                    padding: isMobile ? '6px' : '8px',
                                                    width: isMobile ? '32px' : '36px',
                                                    height: isMobile ? '32px' : '36px',
                                                    cursor: 'pointer',
                                                    backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    color: '#ffffff',
                                                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                                                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(-1px) scale(1.05)';
                                                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                                                }}
                                            >
                                                <Mail size={isMobile ? 14 : 16} />
                                            </button>
                                            <button
                                                onClick={() => handleCancelInvitation(member.id, member.displayName)}
                                                style={{
                                                    border: 'none',
                                                    borderRadius: '999px',
                                                    padding: isMobile ? '6px' : '8px',
                                                    width: isMobile ? '32px' : '36px',
                                                    height: isMobile ? '32px' : '36px',
                                                    cursor: 'pointer',
                                                    backgroundImage: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                                    color: '#ffffff',
                                                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                                                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(-1px) scale(1.05)';
                                                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
                                                }}
                                            >
                                                <XCircle size={isMobile ? 14 : 16} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Confirmation Dialog */}
            {showConfirmDialog && memberToRemove && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                }}>
                    <div style={{
                        backgroundColor: theme.colors.surface,
                        borderRadius: '16px',
                        padding: '20px',
                        maxWidth: '400px',
                        width: '100%',
                        margin: '16px',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                    }}>
                        <h3 style={{
                            color: theme.colors.text,
                            margin: 0,
                            marginBottom: '12px',
                            fontSize: '18px',
                            fontWeight: '600',
                            fontFamily: theme.fonts.primary,
                        }}>
                            Remove Family Member
                        </h3>
                        <p style={{
                            color: theme.colors.textSecondary,
                            margin: 0,
                            marginBottom: '20px',
                            fontSize: '14px',
                            fontWeight: '400',
                            fontFamily: theme.fonts.primary,
                            lineHeight: '1.5',
                        }}>
                            Are you sure you want to remove <strong style={{ color: theme.colors.text, fontWeight: '600' }}>{memberToRemove.name}</strong> from your family members?
                        </p>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={cancelRemoveFamilyMember}
                                style={{
                                    border: '1px solid rgba(148, 163, 184, 0.5)',
                                    borderRadius: '999px',
                                    padding: '8px 18px',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    backgroundColor: 'transparent',
                                    color: 'inherit',
                                    transition: 'all 0.25s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(148, 163, 184, 0.1)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmRemoveFamilyMember}
                                style={{
                                    border: 'none',
                                    borderRadius: '999px',
                                    padding: '8px 18px',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    backgroundImage: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                    color: '#ffffff',
                                    boxShadow: '0 8px 20px rgba(239, 68, 68, 0.35)',
                                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                    e.currentTarget.style.boxShadow = '0 12px 28px rgba(239, 68, 68, 0.45)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(239, 68, 68, 0.35)';
                                }}
                            >
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: '-100%',
                                        width: '100%',
                                        height: '100%',
                                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)',
                                        animation: 'shine 2s infinite',
                                    }}
                                />
                                <span style={{ position: 'relative', zIndex: 1 }}>Remove</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};